import {
  collection, doc, setDoc, getDocs, deleteDoc, updateDoc as fsUpdateDoc,
  query, where, orderBy, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions, fetchGpxWithProxy } from './firebaseService';
import { getDateLocale } from './localeHelpers';
import { TrackPoint, TrackWaypoint, SavedTrack, MapBounds, ElevationPoint, AppUser, Coordinates, RoutePlanAlternative, PlannedRouteMetadata } from '../types';
import { fetchBatchElevations } from './elevationService';

// --- GPX-generering med tid og høyde ---

function pointsToGpx(points: TrackPoint[], name: string, waypoints?: TrackWaypoint[]): string {
  const wpts = (waypoints ?? []).map(wp => {
    const lat = wp.latitude.toFixed(7);
    const lon = wp.longitude.toFixed(7);
    return `  <wpt lat="${lat}" lon="${lon}">\n    <name>${escapeXml(wp.name)}</name>\n    <type>${wp.icon}</type>\n    <time>${new Date(wp.timestamp).toISOString()}</time>\n  </wpt>`;
  }).join('\n');

  const trkpts = points.map(p => {
    const lat = p.latitude.toFixed(7);
    const lon = p.longitude.toFixed(7);
    const time = new Date(p.timestamp).toISOString();
    const ele = p.altitude !== undefined ? `\n      <ele>${p.altitude.toFixed(1)}</ele>` : '';
    return `    <trkpt lat="${lat}" lon="${lon}">${ele}\n      <time>${time}</time>\n    </trkpt>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Fricamp Turlogg" xmlns="http://www.topografix.com/GPX/1/1">
${wpts ? wpts + '\n' : ''}  <trk>
    <name>${escapeXml(name)}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
}

function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// --- Beregn bounds fra punkter ---

function calcBounds(points: TrackPoint[]): MapBounds {
  const lats = points.map(p => p.latitude);
  const lons = points.map(p => p.longitude);
  return {
    latMin: Math.min(...lats),
    latMax: Math.max(...lats),
    lonMin: Math.min(...lons),
    lonMax: Math.max(...lons),
  };
}

// --- Firestore CRUD ---

export interface TrackStats {
  distance: number;
  duration: number;
  elevationGain: number;
  elevationLoss: number;
}

export async function saveTrack(
  userId: string,
  name: string,
  points: TrackPoint[],
  stats: TrackStats,
  waypoints?: TrackWaypoint[],
  followedRouteId?: string
): Promise<SavedTrack> {
  if (points.length === 0) throw new Error('Ingen GPS-punkter å lagre');

  const trackId = `${userId}_${Date.now()}`;

  // 1. Generer GPX og last opp til Cloud Storage
  const gpxContent = pointsToGpx(points, name, waypoints);
  const gpxBlob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const storageRef = ref(storage, `user_tracks/${userId}/${trackId}.gpx`);
  const snap = await uploadBytes(storageRef, gpxBlob);
  const gpxUrl = await getDownloadURL(snap.ref);

  // 2. Beregn metadata
  const bounds = calcBounds(points);
  const startLocation = { latitude: points[0].latitude, longitude: points[0].longitude };

  // 3. Lagre metadata i Firestore
  const track: SavedTrack = {
    id: trackId,
    userId,
    name,
    createdAt: Date.now(),
    distance: Math.round(stats.distance),
    duration: stats.duration,
    elevationGain: stats.elevationGain,
    elevationLoss: stats.elevationLoss,
    pointCount: points.length,
    gpxUrl,
    startLocation,
    bounds,
    ...(waypoints && waypoints.length > 0 ? { waypoints } : {}),
    ...(followedRouteId ? { followedRouteId } : {}),
  };

  await setDoc(doc(db, 'user_tracks', trackId), {
    ...track,
    _serverTs: serverTimestamp(),
  });

  // 4. Fjern lokal backup
  try { localStorage.removeItem('villmark_cache_active_track_points'); } catch { /* ignore */ }
  try { localStorage.removeItem('villmark_cache_active_track_state'); } catch { /* ignore */ }

  return track;
}

export async function getUserTracks(userId: string): Promise<SavedTrack[]> {
  const normalizeTrack = (data: any, fallbackId = ''): SavedTrack => ({
    id: data?.id || fallbackId,
    userId: data?.userId || userId,
    name: data?.name || 'Tur',
    createdAt: Number(data?.createdAt || Date.now()),
    distance: Number(data?.distance || 0),
    duration: Number(data?.duration || 0),
    elevationGain: Number(data?.elevationGain || 0),
    elevationLoss: Number(data?.elevationLoss || 0),
    pointCount: Number(data?.pointCount || 0),
    gpxUrl: data?.gpxUrl || '',
    startLocation: data?.startLocation || { latitude: 0, longitude: 0 },
    bounds: data?.bounds || null,
    ...(data?.waypoints ? { waypoints: data.waypoints } : {}),
    ...(data?.linkedRouteId ? { linkedRouteId: data.linkedRouteId } : {}),
    ...(data?.trackType ? { trackType: data.trackType } : {}),
    ...(data?.planStatus ? { planStatus: data.planStatus } : {}),
    ...(data?.plannedRoute ? { plannedRoute: data.plannedRoute } : {}),
    ...(data?.recordedTrackId ? { recordedTrackId: data.recordedTrackId } : {}),
  });

  const fetchViaFirestore = async (): Promise<SavedTrack[]> => {
    const q = query(
      collection(db, 'user_tracks'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => normalizeTrack(d.data(), d.id));
  };

  const fetchViaCallable = async (): Promise<SavedTrack[]> => {
    const callable = httpsCallable(functions, 'listMyTrips');
    const result = await callable({});
    const trips = (result.data as any)?.trips;
    if (!Array.isArray(trips)) return [];
    return trips.map((t: any) => normalizeTrack(t));
  };

  try {
    return await fetchViaFirestore();
  } catch (err: any) {
    const msg = String(err?.message || '');
    const blockedByClient =
      msg.includes('ERR_BLOCKED_BY_CLIENT') ||
      msg.toLowerCase().includes('blocked by client');
    if (!blockedByClient) {
      throw err;
    }
    return await fetchViaCallable();
  }
}

export async function deleteTrack(trackId: string, gpxUrl: string): Promise<void> {
  // Slett Firestore-dokument
  await deleteDoc(doc(db, 'user_tracks', trackId));

  // Slett GPX-fil fra Cloud Storage
  try {
    const storageRef = ref(storage, gpxUrl);
    await deleteObject(storageRef);
  } catch {
    // Ignorer storage-feil (filen finnes kanskje ikke lenger)
  }
}

// --- GPX-eksport som fil-nedlasting ---

export function exportTrackAsGpxDownload(points: TrackPoint[], name: string): void {
  const gpx = pointsToGpx(points, name);
  const blob = new Blob([gpx], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_')}.gpx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// --- Hjelpe-formatering ---

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}t ${m}m`;
  return `${m} min`;
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(getDateLocale(), {
    day: '2-digit', month: 'short', year: 'numeric'
  });
}

// --- Elevation profile utilities ---

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Converts live TrackPoint[] to ElevationPoint[] using GPS altitude.
 * Only returns points with a valid altitude reading.
 */
export function trackPointsToElevationProfile(points: TrackPoint[]): ElevationPoint[] {
  const withAlt = points.filter(p => typeof p.altitude === 'number');
  if (withAlt.length < 2) return [];

  let cumDist = 0;
  return withAlt.map((p, i) => {
    if (i > 0) {
      cumDist += haversineM(withAlt[i - 1].latitude, withAlt[i - 1].longitude, p.latitude, p.longitude);
    }
    return { distance: Math.round(cumDist), elevation: Math.round(p.altitude as number) };
  });
}

/**
 * Fetches a GPX file from Cloud Storage URL and returns an ElevationPoint[] array.
 * Returns [] if the file cannot be fetched or has no elevation data.
 */
export async function parseGpxToElevationProfile(gpxUrl: string): Promise<ElevationPoint[]> {
  try {
    const res = await fetch(gpxUrl);
    if (!res.ok) return [];
    const text = await res.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'application/xml');
    const trkpts = Array.from(xml.querySelectorAll('trkpt'));
    if (trkpts.length < 2) return [];

    let cumDist = 0;
    const result: ElevationPoint[] = [];
    let prevLat: number | null = null;
    let prevLon: number | null = null;

    for (const pt of trkpts) {
      const lat = parseFloat(pt.getAttribute('lat') ?? '');
      const lon = parseFloat(pt.getAttribute('lon') ?? '');
      const eleEl = pt.querySelector('ele');
      const ele = eleEl ? parseFloat(eleEl.textContent ?? '') : NaN;
      if (isNaN(lat) || isNaN(lon) || isNaN(ele)) continue;
      if (prevLat !== null && prevLon !== null) {
        cumDist += haversineM(prevLat, prevLon, lat, lon);
      }
      result.push({ distance: Math.round(cumDist), elevation: Math.round(ele) });
      prevLat = lat;
      prevLon = lon;
    }
    return result;
  } catch {
    return [];
  }
}

// --- Konvertering: tur → rute ---

/**
 * Henter GPX-innholdet fra en Cloud Storage URL og returnerer et File-objekt
 * som kan brukes med uploadGpxRoute.
 */
export async function fetchGpxAsFile(gpxUrl: string, name: string): Promise<File> {
  const text = await fetchGpxWithProxy(gpxUrl);
  const blob = new Blob([text], { type: 'application/gpx+xml' });
  const safeName = name.replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_');
  return new File([blob], `${safeName}.gpx`, { type: 'application/gpx+xml' });
}

/**
 * Parser en GPX-URL og returnerer koordinater, distanse og høydeprofil
 * til bruk med performRouteAnalysis og uploadGpxRoute.
 */
export async function parseGpxCoordinates(gpxUrl: string): Promise<{
  coordinates: [number, number][][];
  distanceKm: number;
  elevationProfile: ElevationPoint[];
}> {
  const text = await fetchGpxWithProxy(gpxUrl);
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'application/xml');

  const trksegs = Array.from(xml.querySelectorAll('trkseg'));
  const segments: [number, number][][] = [];
  const allElevations: number[] = [];

  for (const seg of (trksegs.length > 0 ? trksegs : [xml.documentElement])) {
    const pts = Array.from(seg.querySelectorAll('trkpt'));
    const segCoords: [number, number][] = [];
    for (const pt of pts) {
      const lat = parseFloat(pt.getAttribute('lat') ?? '');
      const lon = parseFloat(pt.getAttribute('lon') ?? '');
      const eleEl = pt.querySelector('ele');
      const ele = eleEl ? parseFloat(eleEl.textContent ?? '') : NaN;
      if (isNaN(lat) || isNaN(lon)) continue;
      segCoords.push([lat, lon]);
      allElevations.push(isNaN(ele) ? 0 : ele);
    }
    if (segCoords.length > 0) segments.push(segCoords);
  }

  // If GPX has no elevation data, fetch from Open-Meteo batch API
  const hasElevation = allElevations.some(e => e > 0);
  if (!hasElevation && segments.length > 0) {
    const allCoords = segments.flat();
    // Sample max ~250 points for API call
    const step = Math.max(1, Math.floor(allCoords.length / 100));
    const sampleCoords = allCoords.filter((_, i) => i % step === 0);
    try {
      const elevations = await fetchBatchElevations(
        sampleCoords.map(([lat, lon]) => ({ latitude: lat, longitude: lon }))
      );
      if (elevations && elevations.length === sampleCoords.length) {
        // Map sampled elevations back to all points via interpolation
        for (let i = 0; i < allCoords.length; i++) {
          const sampleIdx = i / step;
          const lower = Math.floor(sampleIdx);
          const upper = Math.min(lower + 1, elevations.length - 1);
          const frac = sampleIdx - lower;
          allElevations[i] = Math.round(elevations[lower] * (1 - frac) + elevations[upper] * frac);
        }
      }
    } catch (e) {
      console.warn('[parseGpxCoordinates] Elevation enrichment failed:', e);
    }
  }

  // Beregn total distanse og høydeprofil
  let totalDist = 0;
  const profile: ElevationPoint[] = [];
  let flatIdx = 0;
  const sampleRate = Math.max(1, Math.floor(allElevations.length / 100));

  for (const seg of segments) {
    for (let i = 0; i < seg.length; i++) {
      if (i > 0) {
        totalDist += haversineM(seg[i - 1][0], seg[i - 1][1], seg[i][0], seg[i][1]);
      }
      if (flatIdx % sampleRate === 0) {
        profile.push({ distance: Math.round(totalDist), elevation: Math.round(allElevations[flatIdx]) });
      }
      flatIdx++;
    }
  }

  return {
    coordinates: segments,
    distanceKm: Math.round((totalDist / 1000) * 10) / 10,
    elevationProfile: profile,
  };
}

// --- Auto-konvertering: tur → rute ---

/**
 * Konverterer en lagret tur til en privat rute.
 * Kjører ruteanalyse og lagrer som privat GpxRoute med sourceTrackId-referanse.
 */
export async function convertTrackToRoute(track: SavedTrack, user: AppUser): Promise<string | null> {
  // Lazy-import for å unngå sirkulær avhengighet
  const { uploadGpxRoute } = await import('./firebaseService');
  const { performRouteAnalysis, determineRegion } = await import('./routeAnalysis');

  // 1. Hent GPX som File
  const gpxFile = await fetchGpxAsFile(track.gpxUrl, track.name);

  // 2. Parser koordinater fra GPX
  const { coordinates, distanceKm, elevationProfile } = await parseGpxCoordinates(track.gpxUrl);
  if (coordinates.length === 0 || coordinates[0].length < 2) return null;

  // 3. Bestem region fra startpunkt
  const region = determineRegion(coordinates[0][0][0], coordinates[0][0][1]);

  // 4. Kjør ruteanalyse (stille, ingen statusoppdatering)
  const { stats, spots } = await performRouteAnalysis(coordinates);

  // 4b. Calculate elevationGain/Loss from profile if not in stats
  if (elevationProfile.length > 1 && (!stats.elevationGain || stats.elevationGain === 0)) {
    let gain = 0, loss = 0;
    for (let i = 1; i < elevationProfile.length; i++) {
      const diff = elevationProfile[i].elevation - elevationProfile[i - 1].elevation;
      if (diff > 0) gain += diff;
      else loss += Math.abs(diff);
    }
    stats.elevationGain = Math.round(gain);
    stats.elevationLoss = Math.round(loss);
  }

  // 5. Beregn estimert tid (50 km/t for bilruter)
  const hours = distanceKm / 50;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  const estimatedTime = `${h > 0 ? h + 't ' : ''}${m}m`;

  // 6. Konverter track waypoints til route waypoints
  const routeWaypoints = (track.waypoints ?? []).map(wp => ({
    latitude: wp.latitude,
    longitude: wp.longitude,
    name: wp.name,
    icon: wp.icon,
  }));

  // 7. Last opp som privat rute
  const routeId = await uploadGpxRoute(
    user,
    track.name,
    '',               // description
    'Medium',         // difficulty
    gpxFile,
    'private',
    [],               // photos
    region,
    estimatedTime,
    elevationProfile,
    stats,
    [],               // external links
    distanceKm,
    spots,
    undefined,        // startPlace
    undefined,        // endPlace
    routeWaypoints.length > 0 ? routeWaypoints : undefined,
    track.startLocation, // startCoords
    undefined,        // endCoords
    undefined,        // suitability
    undefined,        // seasonal
    track.id,
  );

  return routeId;
}

// --- Planlagt rute ---

function coordsToSimpleGpx(coords: [number, number][], name: string, elevations?: number[]): string {
  const trkpts = coords.map(([lon, lat], i) => {
    const ele = elevations?.[i] != null ? `\n      <ele>${elevations[i]}</ele>` : '';
    return `    <trkpt lat="${lat.toFixed(7)}" lon="${lon.toFixed(7)}">${ele}\n    </trkpt>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Fricamp Ruteplanlegger" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${escapeXml(name)}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
}

export async function savePlannedRoute(
  userId: string,
  name: string,
  alternative: RoutePlanAlternative,
  fromName: string,
  toName: string,
  fromCoords: Coordinates,
  toCoords: Coordinates
): Promise<SavedTrack> {
  const trackId = `${userId}_${Date.now()}`;

  // 1. Fetch elevation data for OSRM coordinates (which lack elevation)
  let elevations: number[] | null = null;
  let elevationGain = 0;
  let elevationLoss = 0;
  try {
    // Sample max 250 points for API call
    const step = Math.max(1, Math.floor(alternative.coordinates.length / 100));
    const sampleCoords = alternative.coordinates.filter((_, i) => i % step === 0);
    const sampleElevations = await fetchBatchElevations(
      sampleCoords.map(([lon, lat]) => ({ latitude: lat, longitude: lon }))
    );
    if (sampleElevations && sampleElevations.length === sampleCoords.length) {
      // Interpolate back to full coordinate array
      elevations = alternative.coordinates.map((_, i) => {
        const sampleIdx = i / step;
        const lower = Math.floor(sampleIdx);
        const upper = Math.min(lower + 1, sampleElevations.length - 1);
        const frac = sampleIdx - lower;
        return Math.round(sampleElevations[lower] * (1 - frac) + sampleElevations[upper] * frac);
      });
      // Calculate gain/loss
      for (let i = 1; i < elevations.length; i++) {
        const diff = elevations[i] - elevations[i - 1];
        if (diff > 0) elevationGain += diff;
        else elevationLoss += Math.abs(diff);
      }
    }
  } catch (e) {
    console.warn('[savePlannedRoute] Elevation enrichment failed:', e);
  }

  // 2. Generer GPX fra OSRM-koordinater med høydedata og last opp
  const gpxContent = coordsToSimpleGpx(alternative.coordinates, name, elevations || undefined);
  const gpxBlob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const storageRef = ref(storage, `user_tracks/${userId}/${trackId}.gpx`);
  const snap = await uploadBytes(storageRef, gpxBlob);
  const gpxUrl = await getDownloadURL(snap.ref);

  // 3. Beregn bounds
  const lats = alternative.coordinates.map(([, lat]) => lat);
  const lons = alternative.coordinates.map(([lon]) => lon);
  const bounds: MapBounds = {
    latMin: Math.min(...lats),
    latMax: Math.max(...lats),
    lonMin: Math.min(...lons),
    lonMax: Math.max(...lons),
  };

  // 3. Bygg metadata
  const plannedRoute: PlannedRouteMetadata = {
    fromName,
    toName,
    fromCoords,
    toCoords,
    durationSeconds: alternative.durationSeconds,
    routeScore: alternative.score,
    scoreBreakdown: alternative.scoreBreakdown,
    fricampSpotCount: alternative.fricampSpotCount,
    attractionCount: alternative.attractionCount,
  };

  const track: SavedTrack = {
    id: trackId,
    userId,
    name,
    createdAt: Date.now(),
    distance: Math.round(alternative.distanceMeters),
    duration: alternative.durationSeconds,
    elevationGain: Math.round(elevationGain),
    elevationLoss: Math.round(elevationLoss),
    pointCount: alternative.coordinates.length,
    gpxUrl,
    startLocation: fromCoords,
    bounds,
    trackType: 'planned',
    planStatus: 'planned',
    plannedRoute,
  };

  await setDoc(doc(db, 'user_tracks', trackId), {
    ...track,
    _serverTs: serverTimestamp(),
  });

  return track;
}

/**
 * Lagrer en planlagt rute som en tur (user_tracks) OG konverterer den umiddelbart
 * til en privat rute (routes) slik at den vises i kartet og sidemenyen.
 */
export async function saveAndConvertPlannedRoute(
  user: AppUser,
  name: string,
  alternative: RoutePlanAlternative,
  fromName: string,
  toName: string,
  fromCoords: Coordinates,
  toCoords: Coordinates
): Promise<{ track: SavedTrack, routeId: string | null }> {
  // 1. Lagre som planlagt tur
  const savedTrack = await savePlannedRoute(
    user.uid,
    name,
    alternative,
    fromName,
    toName,
    fromCoords,
    toCoords
  );

  // 2. Konverter til privat rute for umiddelbar visning
  const routeId = await convertTrackToRoute(savedTrack, user);

  return { track: savedTrack, routeId };
}

export async function updateTrackPlanStatus(
  trackId: string,
  status: 'planned' | 'completed' | 'both',
  recordedTrackId?: string
): Promise<void> {
  const update: Record<string, any> = { planStatus: status };
  if (recordedTrackId) update.recordedTrackId = recordedTrackId;
  await fsUpdateDoc(doc(db, 'user_tracks', trackId), update);
}
